import snakeCase from 'lodash/snakeCase';
import { useApiQuery } from 'src/api';
import { useBuildVars } from 'src/config';
import {
  useQueryParams,
  parseResponse,
  QueryResult,
  SearchQuery,
} from './hooks';

export const CUSTOMER_GROUP_SEARCH_URL = '/';

export const CustomerGroupSearchFilters = [
  'name',
  'id',
  'customerName',
  'customerNumber',
  'masterCustomerName',
  'masterCustomerNumber',
] as const;
export type CustomerGroupSearchFilter =
  typeof CustomerGroupSearchFilters[number];

export type CustomerGroupListResult = QueryResult<CustomerGroup>;

export interface CustomerGroup {
  createdAt: string;
  customerCount: number;
  description: string;
  id: string;
  lastUpdated: string;
  name: string;
  status: boolean;
  vendorProgramCount: number;
}

export type UseCustomerGroupQueryParams = Partial<
  SearchQuery<CustomerGroupSearchFilter>
>;

export function useCustomerGroupQueryParams({
  filter,
  pageSize: initialPageSize,
}: UseCustomerGroupQueryParams = {}) {
  const {
    searchQuery: { sort, ...searchQuery },
    ...rest
  } = useQueryParams<CustomerGroupSearchFilter, keyof CustomerGroup>({
    filter: filter ?? {
      field: 'name' as const,
      value: '',
    },
    pageSize: initialPageSize,
    sort: {
      field: 'name' as const,
      sort: 'asc' as const,
    },
  });

  return {
    searchQuery: {
      ...searchQuery,
      sort: `${rest.sort?.sort?.sort === 'desc' ? '-' : ''}${snakeCase(
        rest.sort?.sort?.field
      )}`,
    },
    ...rest,
  };
}

export function useCustomerGroupSearchQuery(
  { ...query }: any,
  useErrorBoundary?: (() => boolean) | boolean,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  return useApiQuery<CustomerGroupListResult>(
    CUSTOMER_GROUP_SEARCH_URL,
    query,
    {
      enabled: enabled ?? true,
      useErrorBoundary:
        useErrorBoundary ??
        ((error) => ![404, 400].includes(error?.status as number)),
    },
    undefined,
    {
      host: buildVars.CG_API_BASE,
      parseResponse: (res) => parseResponse<CustomerGroup>(res),
    }
  );
}
